import { useEffect, useState } from 'react';
import {
  DESKTOP_RELEASES_URL,
  EMPTY_RELEASES,
  parseDesktopReleases,
  type DesktopReleases,
} from './companionDownload';

export type DesktopReleasesStatus = 'loading' | 'ready' | 'unavailable';

/**
 * The desktop release manifest, shared by the public download page and Settings → Extensions.
 * `unavailable` means the manifest could not be read (network or HTTP error); a manifest
 * that loads with empty urls is `ready` and means "not released yet".
 */
export function useDesktopReleases(enabled = true): { releases: DesktopReleases; status: DesktopReleasesStatus } {
  const [releases, setReleases] = useState<DesktopReleases>(EMPTY_RELEASES);
  const [status, setStatus] = useState<DesktopReleasesStatus>('loading');

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    fetch(DESKTOP_RELEASES_URL, { cache: 'no-store', signal: controller.signal })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then((raw) => {
        if (controller.signal.aborted) return;
        setReleases(parseDesktopReleases(raw));
        setStatus('ready');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.warn('[companion] desktop release manifest unavailable', error);
        setStatus('unavailable');
      });
    return () => controller.abort();
  }, [enabled]);

  return { releases, status };
}
